import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import usePrivateLink from "../../config/usePrivateLink";
import Header from "../HostelHeader";
import Nav from "../../master/NavBar";

import {
  formatDate,
  SweetAlertShow,
  LoadingDiv,
  ToastMain,
} from "../../master/Master";

import { imgBaseUrl } from "../../../api/api";
import {
  getDetails,
  HostelFeeGeneratePreview,
  HostelFeeGenerate,
} from "../../../api/HostelService";

import CourseNFeeTab from "./CourseNFeeTab";
import FeeTransactions from "../../student/tabs/FeeTransactions";

const StudentProfile2 = () => {
  const hasAccess = usePrivateLink();
  const location = useLocation();

  const [Tab, setTab] = useState("CourseNFee");
  const [loading, setLoading] = useState(false);
  const [Data, setData] = useState(null);
  const [hostel, setHostel] = useState(null);

  /* ================================================================
   MONTHLY FEE GENERATION
================================================================ */

  const [showGenerateFeeModal, setShowGenerateFeeModal] = useState(false);
  const [generateMonth, setGenerateMonth] = useState("");

  const [generatePreview, setGeneratePreview] = useState(null);
  const [generateRows, setGenerateRows] = useState([]);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [generatingFee, setGeneratingFee] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const studentId = queryParams.get("id");

  /* ================================================================
     FETCH STUDENT
  ================================================================ */

  useEffect(() => {
    if (studentId) {
      getStudent();
    }
  }, [studentId]);

  const getStudent = async () => {
    setLoading(true);

    try {
      const response = await getDetails(studentId);

      if (response?.data?.status) {
        const data = response?.data?.data;

        setData(data);
        setHostel(response?.data);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Something went wrong!",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.response?.message ||
        error?.message ||
        "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ================================================================
     DATA
  ================================================================ */

  const student = Data?.student || {};
  const hostelInfo = Data?.hostel || {};
  const room = Data?.room || {};
  const bed = Data?.bed || {};

  const hostelFee = Data?.hostel_fees?.[0] || {};

  const totalFee =
    hostelFee?.total_amount !== null && hostelFee?.total_amount !== undefined
      ? Number(hostelFee.total_amount)
      : Number(Data?.total_fee || 0);

  const finalFee =
    hostelFee?.final_amount !== null && hostelFee?.final_amount !== undefined
      ? Number(hostelFee.final_amount)
      : Number(Data?.final_fee || 0);

  const paidFee =
    hostelFee?.paid_amount !== null && hostelFee?.paid_amount !== undefined
      ? Number(hostelFee.paid_amount)
      : Number(Data?.paid_fee || 0);

  const dueFee =
    hostelFee?.left_amount !== null && hostelFee?.left_amount !== undefined
      ? Number(hostelFee.left_amount)
      : Math.max(finalFee - paidFee, 0);

  const advanceFee = Number(Data?.advance_fee || 0);

  const paymentPercentage =
    finalFee > 0 ? Math.min(Math.round((paidFee / finalFee) * 100), 100) : 0;

  const statusActive =
    String(Data?.status) === "1" || String(student?.status) === "1";

  /* ================================================================
     PROFILE
  ================================================================ */

  const getInitial = () => {
    const name = Data?.student_name || student?.name || "U";

    return name.charAt(0).toUpperCase();
  };

  const profileImage = student?.profile_picture
    ? `${imgBaseUrl}${student.profile_picture}`
    : null;

  /* ================================================================
     TABS
  ================================================================ */

  const tabs = [
    {
      key: "CourseNFee",
      label: "Hostel Details",
      icon: "ri-home-5-line",
      show: hasAccess("/student/course_fee"),
    },
    {
      key: "Transaction",
      label: "Transactions",
      icon: "ri-receipt-line",
      show:
        hasAccess("/fees/transactions") ||
        hasAccess("/student/fee/transaction/delete") ||
        hasAccess("/student/fees/receipt/print"),
    },
  ];

  /* ================================================================
   MONTHLY FEE GENERATION
================================================================ */

  const openGenerateFeeModal = () => {
    setGenerateMonth("");
    setGeneratePreview(null);
    setGenerateRows([]);
    setShowGenerateFeeModal(true);
  };

  const closeGenerateFeeModal = () => {
    if (previewLoading || generatingFee) return;

    setShowGenerateFeeModal(false);
    setGenerateMonth("");
    setGeneratePreview(null);
    setGenerateRows([]);
  };

  const fetchStudentFeePreview = async () => {
    if (!generateMonth) {
      SweetAlertShow(
        "info",
        "Select Month",
        "Please select the month for which you want to generate the fee.",
      );
      return;
    }

    /*
     * IMPORTANT:
     * Fee generation is based on hostel_students.id
     * i.e. hostel_student_id.
     *
     * Do NOT use student.id / student_id here.
     */
    const hostelStudentId =
      Data?.hostel_student_id || Data?.hostel_student?.id || Data?.id;

    const hostelId = hostelInfo?.id || Data?.hostel_id;

    if (!hostelStudentId) {
      SweetAlertShow(
        "error",
        "Hostel Student ID Missing",
        "Hostel student record ID is not available for fee generation.",
      );
      return;
    }

    if (!hostelId) {
      SweetAlertShow(
        "error",
        "Hostel Missing",
        "Hostel information is not available.",
      );
      return;
    }

    setPreviewLoading(true);
    setGeneratePreview(null);
    setGenerateRows([]);

    try {
      const response = await HostelFeeGeneratePreview({
        hostel_id: hostelId,
        hostel_student_id: hostelStudentId,
        month: generateMonth,
      });

      if (response?.data?.status) {
        const data = response?.data?.data;

        setGeneratePreview(data);

        setGenerateRows(
          (data?.pending || []).map((item) => ({
            hostel_student_id: item.hostel_student_id,
            student_name: item.student_name,
            registration_no: item.registration_no,
            total_amount: item.total_amount,
            amount: item.default_amount,
            is_prorated: item.is_prorated,
            advance_fee: item.advance_fee || 0,
            checked: true,
          })),
        );
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to generate fee preview.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load fee preview.",
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const updateGenerateAmount = (value) => {
    setGenerateRows((prev) =>
      prev.map((row) => ({
        ...row,
        amount: value,
      })),
    );
  };

  const submitStudentFeeGenerate = async () => {
    if (!generateRows.length) {
      SweetAlertShow(
        "info",
        "Nothing to Generate",
        "There is no pending fee available for this month.",
      );
      return;
    }

    const row = generateRows[0];

    const amount = Number(row.amount);

    if (Number.isNaN(amount) || amount < 0) {
      SweetAlertShow(
        "info",
        "Invalid Amount",
        "Please enter a valid fee amount.",
      );
      return;
    }

    const hostelId = hostelInfo?.id || Data?.hostel_id;

    if (!hostelId) {
      SweetAlertShow(
        "error",
        "Hostel Missing",
        "Hostel information is not available.",
      );
      return;
    }

    setGeneratingFee(true);

    try {
      const response = await HostelFeeGenerate({
        hostel_id: hostelId,
        month: generateMonth,

        students: [
          {
            hostel_student_id: row.hostel_student_id,
            amount: amount,
          },
        ],
      });

      if (response?.data?.status) {
        const { created = [], skipped = [] } = response?.data?.data || {};

        ToastMain.fire({
          icon: "success",
          title:
            `${created.length} fee record(s) generated` +
            (skipped.length ? `, ${skipped.length} skipped` : "") +
            ".",
        });

        closeGenerateFeeModal();

        /*
         * Refresh complete student profile so:
         * - fee amount updates
         * - paid/due updates
         * - latest month appears
         */
        await getStudent();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Fee generation failed.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to generate hostel fee.",
      );
    } finally {
      setGeneratingFee(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-xxl flex-grow-1 container-p-y">
              {/* =====================================================
                  STUDENT PROFILE HEADER
              ===================================================== */}

              <div className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden">
                {/* Cover */}
                <div
                  className="student-cover position-relative"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--bs-primary), rgba(var(--bs-primary-rgb), .62))",
                  }}
                >
                  <div className="student-cover-glow" />

                  <div className="position-absolute top-0 end-0 p-3">
                    <span className="badge bg-white text-primary rounded-pill px-3 py-2 shadow-sm">
                      <i className="ri-home-5-line me-1" />
                      Hostel Student
                    </span>
                  </div>
                </div>

                {/* Profile Body */}
                <div className="card-body student-profile-body">
                  <div className="d-flex flex-column flex-md-row align-items-center align-items-md-end">
                    {/* Avatar */}
                    <div
                      className="student-avatar-wrapper position-relative"
                      style={{
                        cursor: profileImage ? "pointer" : "default",
                      }}
                      data-bs-toggle={profileImage ? "modal" : undefined}
                      data-bs-target={profileImage ? "#imgModal" : undefined}
                    >
                      <div className="student-avatar">
                        {profileImage ? (
                          <img src={profileImage} alt="Student" />
                        ) : (
                          <div className="student-avatar-placeholder">
                            {getInitial()}
                          </div>
                        )}
                      </div>

                      <span
                        className={`student-status-dot ${statusActive ? "bg-success" : "bg-danger"
                          }`}
                      />
                    </div>

                    {/* Student Name */}
                    <div className="flex-grow-1 text-center text-md-start student-main-info">
                      <div className="d-flex flex-column flex-md-row align-items-center gap-2">
                        <h4 className="mb-0 fw-bold text-uppercase student-name">
                          {Data?.student_name || student?.name || "Student"}
                        </h4>

                        <span
                          className={`badge rounded-pill ${statusActive
                            ? "bg-label-success"
                            : "bg-label-danger"
                            }`}
                        >
                          <i
                            className={`ri-checkbox-blank-circle-fill me-1 ${statusActive ? "text-success" : "text-danger"
                              }`}
                            style={{
                              fontSize: "6px",
                            }}
                          />

                          {statusActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <div className="text-muted student-registration">
                        <i className="ri-id-card-line me-1" />
                        Registration No.
                        <span className="fw-semibold ms-1">
                          {Data?.registration_no || "—"}
                        </span>
                      </div>
                    </div>

                    {/* Category / Gender */}
                    <div className="d-flex gap-2 student-badges">
                      {student?.category && (
                        <span className="badge bg-label-info rounded-pill px-3 py-2">
                          {student.category}
                        </span>
                      )}

                      {student?.gender && (
                        <span className="badge bg-label-success rounded-pill px-3 py-2">
                          {student.gender}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick Information */}
                  <div className="row g-2 student-quick-info">
                    <div className="col-6 col-md-3">
                      <QuickInfo
                        icon="ri-user-line"
                        label="Father's Name"
                        value={Data?.father_name}
                        color="primary"
                      />
                    </div>

                    <div className="col-6 col-md-3">
                      <QuickInfo
                        icon="ri-phone-line"
                        label="Mobile"
                        value={Data?.mobile || student?.phone}
                        color="warning"
                      />
                    </div>

                    <div className="col-6 col-md-3">
                      <QuickInfo
                        icon="ri-calendar-check-line"
                        label="Joining Date"
                        value={
                          Data?.joining_date
                            ? formatDate(Data.joining_date)
                            : "—"
                        }
                        color="success"
                      />
                    </div>

                    <div className="col-6 col-md-3">
                      <QuickInfo
                        icon="ri-map-pin-line"
                        label="District"
                        value={student?.district || "—"}
                        color="info"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* =====================================================
                  HOSTEL SUMMARY
              ===================================================== */}

              <div className="row g-2 mb-3">
                {/* Hostel */}
                <div className="col-xl-3 col-md-6">
                  <SummaryCard
                    icon="ri-home-5-line"
                    color="primary"
                    title="Hostel"
                    value={hostelInfo?.name || "—"}
                    subtitle={hostelInfo?.address || "Address unavailable"}
                  />
                </div>

                {/* Room */}
                <div className="col-xl-3 col-md-6">
                  <SummaryCard
                    icon="ri-door-open-line"
                    color="warning"
                    title="Room"
                    value={room?.room_no || "—"}
                    subtitle="Room Number"
                  />
                </div>

                {/* Bed */}
                <div className="col-xl-3 col-md-6">
                  <SummaryCard
                    icon="ri-hotel-bed-line"
                    color="danger"
                    title="Bed"
                    value={bed?.bed_no || "—"}
                    subtitle={bed?.status || "Bed Status"}
                    badge={bed?.status}
                  />
                </div>

                {/* Due */}
                <div className="col-xl-3 col-md-6">
                  <SummaryCard
                    icon="ri-money-rupee-circle-line"
                    color="warning"
                    title="Hostel Fee Due"
                    value={`₹${dueFee.toLocaleString("en-IN")}`}
                    subtitle={
                      Data?.fee_type ? `${Data.fee_type} Fee` : "Fee Due"
                    }
                  />
                </div>
              </div>

              {/* =====================================================
                  DETAILS + FEE
              ===================================================== */}

              <div className="row g-3 mb-3">
                {/* Student Details */}
                <div className="col-xl-4">
                  <div className="card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body compact-card-body">
                      <div className="section-heading">
                        <div>
                          <h6 className="mb-1 fw-bold">Student Details</h6>

                          <small className="text-muted">
                            Basic student information
                          </small>
                        </div>

                        <div className="section-heading-icon bg-label-primary">
                          <i className="ri-user-3-line text-primary" />
                        </div>
                      </div>

                      <div className="info-grid">
                        <InfoRow
                          icon="ri-women-line"
                          label="Mother's Name"
                          value={student?.mother_name}
                          color="info"
                        />

                        <InfoRow
                          icon="ri-calendar-line"
                          label="Date of Birth"
                          value={student?.dob ? formatDate(student.dob) : "—"}
                          color="danger"
                        />

                        <InfoRow
                          icon="ri-user-settings-line"
                          label="Category"
                          value={student?.category}
                          color="success"
                        />

                        <InfoRow
                          icon="ri-genderless-line"
                          label="Gender"
                          value={student?.gender}
                          color="warning"
                        />

                        <InfoRow
                          icon="ri-map-pin-line"
                          label="Address"
                          value={Data?.address}
                          color="primary"
                          fullWidth
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hostel Fee */}
                <div className="col-xl-8">
                  <div className="card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body compact-card-body">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
                        <div>
                          <h6 className="mb-1 fw-bold">
                            Hostel & Fee Overview
                          </h6>

                          <small className="text-muted">
                            Current accommodation and payment summary
                          </small>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="badge bg-label-primary rounded-pill">
                            {Data?.fee_type || "Full"} Fee
                          </span>

                          {hostelFee?.month && (
                            <span className="badge bg-label-secondary rounded-pill">
                              {hostelFee.month}
                            </span>
                          )}

                          {String(Data?.fee_type || "").toLowerCase() ===
                            "monthly" && (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm rounded-pill px-3"
                                onClick={openGenerateFeeModal}
                                disabled={!statusActive}
                              >
                                <i className="ri-add-circle-line me-1" />
                                Generate Fee
                              </button>
                            )}
                        </div>
                      </div>

                      {/* Fee Boxes */}
                      <div className="row g-2">
                        <FeeBox
                          title="Total Fee"
                          value={totalFee}
                          icon="ri-wallet-3-line"
                          color="primary"
                        />

                        <FeeBox
                          title="Final Fee"
                          value={finalFee}
                          icon="ri-price-tag-3-line"
                          color="info"
                        />

                        <FeeBox
                          title="Paid"
                          value={paidFee}
                          icon="ri-checkbox-circle-line"
                          color="success"
                        />

                        <FeeBox
                          title="Due"
                          value={dueFee}
                          icon="ri-error-warning-line"
                          color="danger"
                        />
                        <FeeBox
                          title="Advance"
                          value={advanceFee}
                          icon="ri-wallet-3-line"
                          color="success"
                        />
                      </div>

                      {/* Payment Progress */}
                      <div className="payment-progress">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted">
                            Fee Payment Progress
                          </small>

                          <small className="fw-semibold">
                            {paymentPercentage}%
                          </small>
                        </div>

                        <div
                          className="progress"
                          style={{
                            height: "7px",
                            borderRadius: "20px",
                          }}
                        >
                          <div
                            className="progress-bar bg-success"
                            role="progressbar"
                            style={{
                              width: `${paymentPercentage}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* =====================================================
                  TABS
              ===================================================== */}

              <div className="card border-0 shadow-sm rounded-4">
                <div className="card-header border-0 bg-transparent p-2">
                  <div className="student-profile-tabs">
                    {tabs
                      .filter((tab) => tab.show)
                      .map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          className={`student-profile-tab ${Tab === tab.key ? "active" : ""
                            }`}
                          onClick={() => setTab(tab.key)}
                        >
                          <i className={`${tab.icon} me-2`} />

                          {tab.label}
                        </button>
                      ))}
                  </div>
                </div>

                <div className="card-body tab-content-body">
                  {Tab === "CourseNFee" && (
                    <CourseNFeeTab data={hostel} getStudent={getStudent} />
                  )}

                  {Tab === "Transaction" && (
                    <FeeTransactions
                      setLoading={setLoading}
                      data={Data}
                      handleFunction={getStudent}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =============================================================
    MONTHLY FEE GENERATION MODAL
============================================================= */}

      {showGenerateFeeModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,.55)",
          }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            role="document"
          >
            <div className="modal-content border-0 rounded-4 shadow-lg overflow-hidden">
              {/* HEADER */}
              <div className="modal-header border-0 pb-2">
                <div>
                  <h5 className="modal-title fw-bold mb-1">
                    <i className="ri-money-rupee-circle-line text-primary me-2" />
                    Generate Monthly Fee
                  </h5>

                  <small className="text-muted">
                    Generate hostel fee for this student
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closeGenerateFeeModal}
                  disabled={previewLoading || generatingFee}
                />
              </div>

              {/* BODY */}
              <div className="modal-body pt-2">
                {/* STUDENT INFO */}
                <div className="student-fee-generation-info mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <div className="student-fee-generation-icon">
                      <i className="ri-user-3-line" />
                    </div>

                    <div className="flex-grow-1">
                      <div className="fw-bold">
                        {Data?.student_name || student?.name || "Student"}
                      </div>

                      <div className="text-muted small">
                        Registration No:{" "}
                        <span className="fw-semibold">
                          {Data?.registration_no || "—"}
                        </span>
                      </div>
                    </div>

                    <span className="badge bg-label-primary rounded-pill">
                      Monthly
                    </span>
                  </div>
                </div>

                {/* MONTH SELECTION */}
                <div className="row g-3 align-items-end">
                  <div className="col-md-8">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="month"
                        className="form-control"
                        value={generateMonth}
                        onChange={(e) => {
                          setGenerateMonth(e.target.value);

                          /*
                           * If user changes month after preview,
                           * clear old preview.
                           */
                          setGeneratePreview(null);
                          setGenerateRows([]);
                        }}
                        disabled={previewLoading || generatingFee}
                      />

                      <label>Select Month</label>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <button
                      type="button"
                      className="btn btn-outline-primary w-100"
                      onClick={fetchStudentFeePreview}
                      disabled={
                        previewLoading || generatingFee || !generateMonth
                      }
                    >
                      {previewLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          Previewing...
                        </>
                      ) : (
                        <>
                          <i className="ri-search-line me-1" />
                          Preview Fee
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* PREVIEW */}
                {generatePreview && (
                  <div className="mt-4">
                    {/* SUMMARY */}
                    <div className="student-fee-preview-summary mb-3">
                      <div>
                        <span>Month</span>
                        <strong>{generatePreview.month}</strong>
                      </div>

                      <div>
                        <span>Fee Type</span>
                        <strong>Monthly</strong>
                      </div>

                      <div>
                        <span>Status</span>

                        <strong
                          className={
                            generateRows.length ? "text-success" : "text-muted"
                          }
                        >
                          {generateRows.length ? "Ready" : "Already Generated"}
                        </strong>
                      </div>
                    </div>

                    {/* NO PENDING FEE */}
                    {generateRows.length === 0 ? (
                      <div className="student-fee-empty-state">
                        <div className="student-fee-empty-icon">
                          <i className="ri-checkbox-circle-line" />
                        </div>

                        <h6 className="mb-1">Fee Already Generated</h6>

                        <p className="text-muted mb-0">
                          The hostel fee for{" "}
                          <strong>{generatePreview.month}</strong> has already
                          been generated for this student.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* FEE PREVIEW CARD */}
                        <div className="student-fee-preview-card">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <small className="text-muted d-block">
                                Fee Month
                              </small>

                              <h6 className="fw-bold mb-0">
                                {generatePreview.month}
                              </h6>
                            </div>

                            {generateRows[0]?.is_prorated && (
                              <span className="badge bg-label-info rounded-pill">
                                Prorated
                              </span>
                            )}
                          </div>

                          <div className="row g-3">
                            <div className="col-md-4">
                              <div className="fee-generation-stat">
                                <small>Standard Fee</small>

                                <strong>
                                  ₹
                                  {Number(
                                    generateRows[0]?.total_amount || 0,
                                  ).toLocaleString("en-IN")}
                                </strong>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="fee-generation-stat">
                                <small>Advance Available</small>

                                <strong className="text-info">
                                  ₹
                                  {Number(
                                    generateRows[0]?.advance_fee || 0,
                                  ).toLocaleString("en-IN")}
                                </strong>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="fee-generation-stat">
                                <small>Fee To Generate</small>

                                <strong className="text-primary">
                                  ₹
                                  {Number(
                                    generateRows[0]?.amount || 0,
                                  ).toLocaleString("en-IN")}
                                </strong>
                              </div>
                            </div>
                          </div>

                          <hr />

                          {/* AMOUNT */}
                          <div className="form-floating form-floating-outline">
                            <input
                              type="number"
                              className="form-control"
                              min="0"
                              step="0.01"
                              value={generateRows[0]?.amount ?? ""}
                              onChange={(e) =>
                                updateGenerateAmount(e.target.value)
                              }
                              disabled={generatingFee}
                            />

                            <label>Fee Amount</label>
                          </div>

                          <small className="text-muted mt-2 d-block">
                            You can adjust the fee amount before generating.
                          </small>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* FOOTER */}
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  onClick={closeGenerateFeeModal}
                  disabled={previewLoading || generatingFee}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitStudentFeeGenerate}
                  disabled={
                    generatingFee || previewLoading || generateRows.length === 0
                  }
                >
                  {generatingFee ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="ri-add-circle-line me-1" />
                      Generate Fee
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showGenerateFeeModal && (
        <div
          className="modal-backdrop fade show"
          onClick={
            previewLoading || generatingFee ? undefined : closeGenerateFeeModal
          }
        />
      )}

      {/* =============================================================
          PROFILE IMAGE MODAL
      ============================================================= */}

      <div
        className="modal fade"
        id="imgModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 overflow-hidden">
            <div className="modal-header border-0 pb-2">
              <div>
                <h5 className="modal-title fw-bold">Student Profile</h5>

                <small className="text-muted">{Data?.student_name}</small>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>

            <div className="modal-body text-center pt-1 pb-4">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Student Profile"
                  className="img-fluid rounded-4 shadow-sm"
                  style={{
                    maxHeight: "650px",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div
                  className="rounded-circle mx-auto d-flex align-items-center justify-content-center bg-label-primary text-primary fw-bold"
                  style={{
                    width: "180px",
                    height: "180px",
                    fontSize: "70px",
                  }}
                >
                  {getInitial()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* =============================================================
          STYLES
      ============================================================= */}

      <style>{`

        /* ============================================================
           PROFILE COVER
        ============================================================ */

        .student-cover {
          height: 95px;
          overflow: hidden;
        }

        .student-cover-glow {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(
              circle at 85% 20%,
              rgba(255,255,255,.20),
              transparent 32%
            );
        }

        .student-profile-body {
          padding: 0 18px 16px;
        }


        /* ============================================================
           AVATAR
        ============================================================ */

        .student-avatar-wrapper {
          margin-top: -46px;
          flex-shrink: 0;
        }

        .student-avatar {
          width: 92px;
          height: 92px;
          border-radius: 50%;
          overflow: hidden;
          background: var(--bs-body-bg);
          border: 4px solid var(--bs-body-bg);
          box-shadow: 0 4px 15px rgba(0,0,0,.12);

          display: flex;
          align-items: center;
          justify-content: center;
        }

        .student-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .student-avatar-placeholder {
          width: 100%;
          height: 100%;

          display: flex;
          align-items: center;
          justify-content: center;

          background: rgba(var(--bs-primary-rgb), .12);
          color: var(--bs-primary);

          font-size: 36px;
          font-weight: 700;
        }

        .student-status-dot {
          position: absolute;
          width: 18px;
          height: 18px;

          right: 2px;
          bottom: 4px;

          border-radius: 50%;
          border: 3px solid var(--bs-body-bg);
        }


        /* ============================================================
           NAME / REGISTRATION
        ============================================================ */

        .student-main-info {
          padding: 0 18px 4px;
        }

        .student-name {
          font-size: 20px;
          letter-spacing: .1px;
        }

        .student-registration {
          font-size: 12px;
          margin-top: 4px;
        }

        .student-badges {
          padding-bottom: 5px;
        }


        /* ============================================================
           QUICK INFORMATION
        ============================================================ */

        .student-quick-info {
          margin-top: 14px;
        }

        .profile-mini-info {
          display: flex;
          align-items: center;
          gap: 9px;

          min-height: 52px;

          padding: 8px 10px;

          border-radius: 10px;

          background:
            rgba(var(--bs-primary-rgb), .035);
        }

        .profile-mini-icon {
          width: 32px;
          height: 32px;
          min-width: 32px;

          border-radius: 8px;

          display: flex;
          align-items: center;
          justify-content: center;

          font-size: 15px;
        }

        .profile-mini-info small {
          font-size: 10px;
          line-height: 1.2;
        }

        .profile-mini-info span {
          display: block;

          font-size: 12px;
          font-weight: 600;

          line-height: 1.3;

          margin-top: 2px;

          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }


        /* ============================================================
           SUMMARY CARDS
        ============================================================ */

        .summary-card {
          height: 100%;
          border: 0;
          border-radius: 12px;

          box-shadow:
            0 2px 10px rgba(0,0,0,.045);

          transition:
            transform .18s ease,
            box-shadow .18s ease;
        }

        .summary-card:hover {
          transform: translateY(-2px);

          box-shadow:
            0 5px 16px rgba(0,0,0,.07);
        }

        .summary-card-body {
          padding: 13px 14px;
        }

        .summary-icon {
          width: 38px;
          height: 38px;
          min-width: 38px;

          border-radius: 10px;

          display: flex;
          align-items: center;
          justify-content: center;

          font-size: 18px;
        }

        .summary-title {
          font-size: 10px;
          color: var(--bs-secondary-color);

          margin-bottom: 2px;
        }

        .summary-value {
          font-size: 15px;
          font-weight: 700;

          line-height: 1.25;
        }

        .summary-subtitle {
          font-size: 10px;
          color: var(--bs-secondary-color);

          margin-top: 3px;

          max-width: 190px;

          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }


        /* ============================================================
           DETAILS CARD
        ============================================================ */

        .compact-card-body {
          padding: 16px;
        }

        .section-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;

          margin-bottom: 16px;
        }

        .section-heading h6 {
          font-size: 14px;
        }

        .section-heading small {
          font-size: 10px;
        }

        .section-heading-icon {
          width: 34px;
          height: 34px;

          border-radius: 9px;

          display: flex;
          align-items: center;
          justify-content: center;

          font-size: 16px;
        }


        /* ============================================================
           INFORMATION GRID
        ============================================================ */

        .info-grid {
          display: grid;

          grid-template-columns:
            repeat(2, minmax(0, 1fr));

          gap: 14px 16px;
        }

        .info-grid .full-width {
          grid-column: 1 / -1;
        }

        .info-row {
          display: flex;
          align-items: flex-start;

          gap: 9px;

          min-width: 0;
        }

        .info-row-icon {
          width: 31px;
          height: 31px;
          min-width: 31px;

          border-radius: 8px;

          display: flex;
          align-items: center;
          justify-content: center;

          font-size: 14px;
        }

        .info-row-content {
          min-width: 0;
        }

        .info-row-label {
          display: block;

          color: var(--bs-secondary-color);

          font-size: 10px;

          line-height: 1.2;

          margin-bottom: 2px;
        }

        .info-row-value {
          font-size: 12px;

          font-weight: 600;

          line-height: 1.35;

          word-break: break-word;
        }


        /* ============================================================
           FEE BOXES
        ============================================================ */

        .fee-box {
          height: 100%;

          border: 1px solid
            rgba(var(--bs-secondary-rgb), .12);

          border-radius: 11px;

          padding: 11px 12px;

          transition: all .18s ease;
        }

        .fee-box:hover {
          transform: translateY(-1px);

          box-shadow:
            0 4px 12px rgba(0,0,0,.05);
        }

        .fee-box-title {
          display: block;

          color: var(--bs-secondary-color);

          font-size: 10px;

          margin-bottom: 3px;
        }

        .fee-box-value {
          font-size: 15px;
          font-weight: 700;
        }

        .fee-icon {
          width: 32px;
          height: 32px;

          border-radius: 8px;

          display: flex;
          align-items: center;
          justify-content: center;

          font-size: 15px;
        }

        .payment-progress {
          margin-top: 15px;
        }


        /* ============================================================
           TABS
        ============================================================ */

        .student-profile-tabs {
          display: flex;

          gap: 5px;

          padding: 2px;

          overflow-x: auto;

          scrollbar-width: thin;
        }

        .student-profile-tab {
          border: 0;

          background: transparent;

          padding: 9px 15px;

          border-radius: 9px;

          font-size: 13px;

          font-weight: 500;

          color: var(--bs-secondary-color);

          white-space: nowrap;

          transition: all .18s ease;
        }

        .student-profile-tab:hover {
          background:
            rgba(var(--bs-primary-rgb), .07);

          color: var(--bs-primary);
        }

        .student-profile-tab.active {
          background: var(--bs-primary);

          color: #fff;

          box-shadow:
            0 3px 9px
            rgba(var(--bs-primary-rgb), .22);
        }

        .tab-content-body {
          padding: 14px 16px 16px;
        }


        /* ============================================================
           TABLET
        ============================================================ */

        @media (max-width: 991px) {

          .student-main-info {
            padding-left: 14px;
          }

          .student-name {
            font-size: 18px;
          }

        }


        /* ============================================================
           MOBILE
        ============================================================ */

        @media (max-width: 767px) {

          .student-cover {
            height: 85px;
          }

          .student-profile-body {
            padding:
              0 12px 13px;
          }

          .student-avatar-wrapper {
            margin-top: -40px;
          }

          .student-avatar {
            width: 82px;
            height: 82px;
          }

          .student-avatar-placeholder {
            font-size: 32px;
          }

          .student-main-info {
            padding:
              8px 0 2px;
          }

          .student-badges {
            padding-bottom: 2px;
          }

          .student-quick-info {
            margin-top: 11px;
          }

          .profile-mini-info {
            min-height: 48px;
          }

          .info-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .info-grid .full-width {
            grid-column: auto;
          }

          .compact-card-body {
            padding: 14px;
          }

          .tab-content-body {
            padding: 12px;
          }

        }


        /* ============================================================
           VERY SMALL DEVICES
        ============================================================ */

        @media (max-width: 420px) {

          .student-cover .badge {
            font-size: 10px;
            padding:
              6px 9px !important;
          }

          .student-name {
            font-size: 17px;
          }

          .profile-mini-info span {
            font-size: 11px;
          }

          .summary-card-body {
            padding: 11px;
          }

          .summary-value {
            font-size: 14px;
          }

          .student-profile-tab {
            padding:
              8px 11px;

            font-size: 12px;
          }

        }

        /* ============================================================
   MONTHLY FEE GENERATION
============================================================ */

.student-fee-generation-info {
  padding: 13px 15px;
  border-radius: 12px;
  background: rgba(var(--bs-primary-rgb), 0.055);
  border: 1px solid rgba(var(--bs-primary-rgb), 0.10);
}

.student-fee-generation-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 11px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(var(--bs-primary-rgb), 0.12);
  color: var(--bs-primary);

  font-size: 19px;
}

.student-fee-preview-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.student-fee-preview-summary > div {
  padding: 11px 13px;
  border-radius: 10px;

  background: rgba(var(--bs-primary-rgb), 0.045);
  border: 1px solid rgba(var(--bs-secondary-rgb), 0.10);

  min-width: 0;
}

.student-fee-preview-summary span {
  display: block;
  color: var(--bs-secondary-color);
  font-size: 10px;
  margin-bottom: 3px;
}

.student-fee-preview-summary strong {
  display: block;
  font-size: 13px;
  line-height: 1.3;
}

.student-fee-preview-card {
  padding: 15px;
  border-radius: 12px;

  border: 1px solid rgba(var(--bs-secondary-rgb), 0.12);
  background: var(--bs-body-bg);
}

.fee-generation-stat {
  padding: 10px 12px;
  border-radius: 9px;
  background: rgba(var(--bs-secondary-rgb), 0.045);
  min-height: 62px;
}

.fee-generation-stat small {
  display: block;
  color: var(--bs-secondary-color);
  font-size: 10px;
  margin-bottom: 3px;
}

.fee-generation-stat strong {
  display: block;
  font-size: 14px;
}

.student-fee-empty-state {
  text-align: center;
  padding: 35px 20px;
  border-radius: 12px;

  background: rgba(var(--bs-success-rgb), 0.04);
  border: 1px dashed rgba(var(--bs-success-rgb), 0.22);
}

.student-fee-empty-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 12px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: rgba(var(--bs-success-rgb), 0.10);
  color: var(--bs-success);

  font-size: 25px;
}

@media (max-width: 575px) {

  .student-fee-preview-summary {
    grid-template-columns: 1fr;
  }

  .student-fee-generation-info {
    padding: 11px;
  }

  .student-fee-preview-card {
    padding: 12px;
  }
}

      `}</style>
    </>
  );
};

/* ================================================================
   QUICK INFO
================================================================ */

const QuickInfo = ({ icon, label, value, color = "primary" }) => {
  return (
    <div className="profile-mini-info">
      <div className={`profile-mini-icon bg-label-${color}`}>
        <i className={`${icon} text-${color}`} />
      </div>

      <div className="flex-grow-1 min-w-0">
        <small className="text-muted d-block">{label}</small>

        <span>{value || "—"}</span>
      </div>
    </div>
  );
};

/* ================================================================
   SUMMARY CARD
================================================================ */

const SummaryCard = ({ icon, color, title, value, subtitle, badge }) => {
  return (
    <div className="summary-card">
      <div className="summary-card-body">
        <div className="d-flex align-items-center gap-3">
          <div className={`summary-icon bg-label-${color}`}>
            <i className={`${icon} text-${color}`} />
          </div>

          <div className="flex-grow-1 min-w-0">
            <div className="summary-title">{title}</div>

            <div className="summary-value text-truncate">{value || "—"}</div>

            {badge ? (
              <span
                className={`badge rounded-pill mt-1 ${badge === "Occupied" ? "bg-label-danger" : "bg-label-success"
                  }`}
                style={{
                  fontSize: "9px",
                }}
              >
                {badge}
              </span>
            ) : (
              <div className="summary-subtitle">{subtitle || "—"}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   INFORMATION ROW
================================================================ */

const InfoRow = ({
  icon,
  label,
  value,
  color = "primary",
  fullWidth = false,
}) => {
  return (
    <div className={`info-row ${fullWidth ? "full-width" : ""}`}>
      <div className={`info-row-icon bg-label-${color}`}>
        <i className={`${icon} text-${color}`} />
      </div>

      <div className="info-row-content">
        <span className="info-row-label">{label}</span>

        <div className="info-row-value">{value || "—"}</div>
      </div>
    </div>
  );
};

/* ================================================================
   FEE BOX
================================================================ */

const FeeBox = ({ title, value, icon, color }) => {
  return (
    <div className="col-6 col-md-4">
      <div className="fee-box">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <span className="fee-box-title">{title}</span>

            <div className="fee-box-value">
              ₹{Number(value || 0).toLocaleString("en-IN")}
            </div>
          </div>

          <div className={`fee-icon bg-label-${color}`}>
            <i className={`${icon} text-${color}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProfile2;
